import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

/* Buttons -------------------------------------------------------------- */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  loading?: boolean;
};

const BUTTON_VARIANTS: Record<string, string> = {
  primary: 'bg-jade text-white hover:bg-[#096349] disabled:bg-slate-light',
  secondary: 'bg-white text-ink border border-rule hover:border-slate-light',
  ghost: 'text-slate hover:text-ink hover:bg-white',
  danger: 'bg-rust text-white hover:bg-[#9a1e14]'
};

export function Button({
  variant = 'primary',
  loading = false,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded px-3.5 py-2 text-small font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${BUTTON_VARIANTS[variant]} ${className}`}
    >
      {loading ? 'Working…' : children}
    </button>
  );
}

/* Form fields ---------------------------------------------------------- */

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  /** A caller-supplied control (select, number input, one holding its own state). */
  children?: ReactNode;
};

/**
 * Two shapes, because both are used across the app:
 *
 *   <Field label="Email" {...register('email')} />   renders its own input
 *   <Field label="City"><input … /></Field>          labels the caller's control
 *
 * The ref matters: react-hook-form's `register()` connects to a field through
 * it, so dropping it leaves every field validating as empty. And `children`
 * must never reach the `<input>` — it is a void element, and React throws hard
 * enough to blank the whole page.
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, id, children, ...rest },
  ref
) {
  const errorNode = error ? (
    <span role="alert" className="block text-micro text-rust">
      {error}
    </span>
  ) : null;

  // Nesting associates the label with whatever control the caller passed,
  // without needing to reach in and give it an id.
  if (children) {
    return (
      <label className="block space-y-1.5">
        <span className="block text-small font-medium text-ink">{label}</span>
        {children}
        {errorNode}
      </label>
    );
  }

  const fieldId = id ?? rest.name ?? label;
  return (
    <div className="space-y-1.5">
      <label htmlFor={fieldId} className="block text-small font-medium text-ink">
        {label}
      </label>
      <input
        {...rest}
        ref={ref}
        id={fieldId}
        aria-invalid={Boolean(error)}
        className={`w-full rounded border bg-white px-3 py-2 text-small text-ink placeholder:text-slate-light ${
          error ? 'border-rust' : 'border-rule'
        }`}
      />
      {errorNode}
    </div>
  );
});

/* Surfaces ------------------------------------------------------------- */

export function Panel({
  title,
  action,
  children,
  className = ''
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-lg border border-rule bg-white shadow-panel ${className}`}>
      {title ? (
        <header className="flex items-center justify-between border-b border-rule px-4 py-3">
          <h2 className="text-small font-semibold text-ink">{title}</h2>
          {action}
        </header>
      ) : null}
      {children}
    </section>
  );
}

/* Status --------------------------------------------------------------- */

const STATUS_TONES: Record<string, string> = {
  COMPLETED: 'bg-jade-soft text-jade',
  POS_SYNCED: 'bg-jade-soft text-jade',
  CONFIRMED: 'bg-jade-soft text-jade',
  IN_PROGRESS: 'bg-jade-soft text-jade',
  RINGING: 'bg-jade-soft text-jade',
  TRANSFERRED: 'bg-amber-soft text-amber',
  POS_PENDING: 'bg-amber-soft text-amber',
  ABANDONED: 'bg-rust-soft text-rust',
  FAILED: 'bg-rust-soft text-rust',
  POS_SYNC_FAILED: 'bg-rust-soft text-rust',
  CANCELLED: 'bg-paper text-slate'
};

/** Status text stays sentence case: all-caps labels are noisy in dense tables. */
export const Status = ({ value }: { value: string }) => {
  const label = value.replace(/_/g, ' ').toLowerCase();
  return (
    <span
      className={`inline-block rounded-sm px-2 py-0.5 text-micro font-medium ${
        STATUS_TONES[value] ?? 'bg-paper text-slate'
      }`}
    >
      {label.charAt(0).toUpperCase() + label.slice(1)}
    </span>
  );
};

/* Empty, error and loading states -------------------------------------- */

export const EmptyState = ({
  headline,
  hint,
  action
}: {
  headline: string;
  hint: string;
  action?: ReactNode;
}) => (
  <div className="px-4 py-14 text-center">
    <p className="text-small font-medium text-ink">{headline}</p>
    <p className="mx-auto mt-1 max-w-sm text-small text-slate">{hint}</p>
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);

export const ErrorState = ({ message, onRetry }: { message: string; onRetry?: () => void }) => (
  <div className="px-4 py-12 text-center">
    <p className="text-small font-medium text-rust">{message}</p>
    {onRetry ? (
      <Button variant="secondary" className="mt-4" onClick={onRetry}>
        Try again
      </Button>
    ) : null}
  </div>
);

export const SkeletonRows = ({ rows = 6 }: { rows?: number }) => (
  <div className="divide-y divide-rule" aria-hidden>
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className="flex items-center gap-4 px-4 py-3.5">
        <div className="h-3 w-24 rounded-sm bg-rule" />
        <div className="h-3 w-32 rounded-sm bg-rule" />
        <div className="ml-auto h-3 w-16 rounded-sm bg-rule" />
      </div>
    ))}
  </div>
);
